import * as Sentry from "@sentry/vue";

const DEFAULT_SENTRY_DSN =
    "https://a7d264aed5605924f1349eec4ccc6f9d@o4511648110739456.ingest.de.sentry.io/4511648117424208";

const SENSITIVE_KEY_PATTERN =
    /(^_|payload|values|book|image|photo|email|name|address|phone|localstorage|properties|lineitemproperties|authorization|cookie|token|secret|password)/i;

const IGNORED_MESSAGE_PATTERNS = [
    /^customizer_url_recovered$/i,
    /^builder_content_retry_attempted$/i,
    /^builder_content_reload_attempted$/i,
    /AbortError:\s*Transition was skipped/i,
    /InvalidStateError:\s*Transition was aborted/i,
    /_tfa is not defined/i,
    /Can't find variable:\s*_tfa/i,
    /enableDidUserTypeOnKeyboardLogging/i,
    /window\.webkit\.messageHandlers/i,
    /Could not establish connection\.\s*Receiving end does not exist/i,
    /Failed to fetch.*ecomsend-go\.ecomsend\.com/i,
    /Load failed.*ecomsend-go\.ecomsend\.com/i,
    /Failed to fetch.*otlp-http-production\.shopifysvc\.com/i,
    /Load failed.*otlp-http-production\.shopifysvc\.com/i,
];

const IGNORED_SOURCE_PATTERNS = [
    /^iabjs:\/\//i,
    /^chrome-extension:\/\//i,
    /ecomsend-go\.ecomsend\.com/i,
    /otlp-http-production\.shopifysvc\.com/i,
    /shopifysvc\.com/i,
    /shop_events_listener/i,
    /web-pixels-manager/i,
    /worker\.modern\.js/i,
    /shopify.*web.?pixel/i,
    /config-security\.com/i,
    /consentmo/i,
    /shopify-forms/i,
    /google-analytics\.com/i,
    /googletagmanager\.com/i,
    /doubleclick\.net/i,
    /\/gtm\.js(?:\?|$)/i,
];

const GENERIC_NETWORK_MESSAGE_PATTERNS = [
    /\bNetworkError\b/i,
    /\bLoad failed\b/i,
    /\bFailed to fetch\b/i,
];

const PROTECTED_NETWORK_SOURCE_PATTERNS = [
    /n8n\.dreamytales\.de/i,
    /preview-(?:start|status)-poll/i,
    /\/webhook\//i,
    /builder\.io\/api\/v3\/content/i,
    /\/cart(?:\/|\.js|\?|$)/i,
    /\/cart\/add\.js/i,
    /checkout/i,
    /dodo/i,
];

const safeTagValue = (value) => {
    if (value === undefined || value === null || value === "") return "unknown";
    return String(value).slice(0, 200);
};

const SAFE_TRAFFIC_TAG_PATTERN = /^[A-Za-z0-9._~/-]+$/;
const SAFE_HOST_TAG_PATTERN = /^[A-Za-z0-9.:[\]-]+$/;
const SAFE_SOURCE_ID_PATTERN = /^\d{5,30}$/;

const safeTrafficTagValue = (value, pattern = SAFE_TRAFFIC_TAG_PATTERN) => {
    if (value === undefined || value === null || value === "") return "none";

    const stringValue = String(value);
    if (stringValue.length > 200 || !pattern.test(stringValue)) {
        return "invalid";
    }

    return stringValue;
};

const safeMetricAttributeValue = (value) => {
    if (typeof value === "number" || typeof value === "boolean") {
        return value;
    }

    return safeTagValue(value);
};

const getBrowserLocation = () => {
    if (typeof window === "undefined" || !window.location) return null;
    return window.location;
};

const getDocumentReferrer = () => {
    if (typeof document === "undefined") return "";
    return document.referrer || "";
};

const getHashSearchParams = (hash = "") => {
    const questionIndex = hash.indexOf("?");
    if (questionIndex === -1) return new URLSearchParams();
    return new URLSearchParams(hash.slice(questionIndex + 1));
};

const getMergedSearchParams = (location) => {
    const params = new URLSearchParams(location.search || "");
    const hashParams = getHashSearchParams(location.hash || "");

    hashParams.forEach((value, key) => {
        if (!params.has(key)) {
            params.set(key, value);
        }
    });

    return params;
};

const getHashVariant = (hash = "") => {
    const match = String(hash).match(
        /^#\/([^/?#]+)\/(step[123])(?:[?#].*)?$/
    );
    return match?.[1] || "";
};

const getPathVariant = (pathname = "") => {
    const match = String(pathname).match(
        /^\/([^/?#]+)\/(step[123])\/?$/
    );
    return match?.[1] || "";
};

const getRawVariant = (location, params) =>
    getHashVariant(location.hash || "") ||
    params.get("variant") ||
    getPathVariant(location.pathname || "") ||
    "unknown";

const getRouteSource = (location, params) => {
    if (getHashVariant(location.hash || "")) return "hash";
    if (params.get("variant")) return "query";
    if (getPathVariant(location.pathname || "")) return "path";
    return "none";
};

const getSafeSourceId = (params) => {
    const sourceId = params.get("fbadid") || params.get("tw_adid");
    if (!sourceId) return "none";
    return SAFE_SOURCE_ID_PATTERN.test(sourceId) ? sourceId : "invalid";
};

const getRawSuffix = (rawVariant) => {
    const match = String(rawVariant || "").match(/^\d+_(.+)$/);
    return match?.[1] || "none";
};

const getReferrerParts = (location) => {
    const referrer = getDocumentReferrer();
    if (!referrer) {
        return {
            document_referrer_host: "none",
            document_referrer_path: "none",
        };
    }

    try {
        const referrerUrl = new URL(referrer, location.origin);
        return {
            document_referrer_host: safeTrafficTagValue(
                referrerUrl.host,
                SAFE_HOST_TAG_PATTERN
            ),
            document_referrer_path: safeTrafficTagValue(
                referrerUrl.pathname || "/"
            ),
        };
    } catch {
        // sentry-ignore: an unparsable referrer only affects diagnostic tags
        return {
            document_referrer_host: "invalid",
            document_referrer_path: "invalid",
        };
    }
};

export const getCustomizerTrafficTags = () => {
    const location = getBrowserLocation();
    if (!location) {
        return {
            document_referrer_host: "unknown",
            document_referrer_path: "unknown",
            utm_source: "unknown",
            utm_campaign: "unknown",
            raw_variant: "unknown",
            raw_suffix: "unknown",
            route_source: "unknown",
            host: "unknown",
            fbclid_present: "false",
            gclid_present: "false",
        };
    }

    const params = getMergedSearchParams(location);
    const rawVariant = getRawVariant(location, params);

    return {
        ...getReferrerParts(location),
        utm_source: safeTrafficTagValue(params.get("utm_source")),
        utm_campaign: safeTrafficTagValue(params.get("utm_campaign")),
        raw_variant: safeTrafficTagValue(rawVariant),
        raw_suffix: safeTrafficTagValue(getRawSuffix(rawVariant)),
        route_source: getRouteSource(location, params),
        host: safeTrafficTagValue(location.host, SAFE_HOST_TAG_PATTERN),
        fbclid_present: String(params.has("fbclid")),
        gclid_present: String(params.has("gclid")),
    };
};

export const getCustomizerTrafficContext = () => {
    const location = getBrowserLocation();
    if (!location) return { source_id: "unknown" };

    return {
        source_id: getSafeSourceId(getMergedSearchParams(location)),
    };
};

const getCustomizerContext = (context = {}) => redactSensitiveData({
    ...getCustomizerTrafficContext(),
    ...context,
});

const applyTags = (scope, tags = {}) => {
    Object.entries({
        ...getCustomizerTrafficTags(),
        ...tags,
    }).forEach(([key, value]) => {
        scope.setTag(key, safeTagValue(value));
    });
};

export const redactSensitiveData = (value, depth = 0, keyName = "") => {
    if (depth > 4) return "[Truncated]";

    if (keyName && SENSITIVE_KEY_PATTERN.test(keyName)) {
        return "[Redacted]";
    }

    if (Array.isArray(value)) {
        return value.slice(0, 20).map((item) =>
            redactSensitiveData(item, depth + 1)
        );
    }

    if (value && typeof value === "object") {
        return Object.entries(value).reduce((result, [key, nestedValue]) => {
            result[key] = redactSensitiveData(nestedValue, depth + 1, key);
            return result;
        }, {});
    }

    if (typeof value === "string" && value.length > 500) {
        return `${value.slice(0, 500)}...[Truncated]`;
    }

    return value;
};

const stringMatchesAny = (value, patterns) => (
    typeof value === "string" && patterns.some((pattern) => pattern.test(value))
);

const getExceptionValues = (event) => event.exception?.values || [];

const getExceptionFrames = (event) =>
    getExceptionValues(event).flatMap((exception) =>
        exception.stacktrace?.frames || []
    );

const flattenPrimitiveValues = (value, depth = 0) => {
    if (depth > 3 || value === undefined || value === null) return [];
    if (["string", "number", "boolean"].includes(typeof value)) {
        return [String(value)];
    }
    if (Array.isArray(value)) {
        return value.flatMap((item) => flattenPrimitiveValues(item, depth + 1));
    }
    if (typeof value === "object") {
        return Object.entries(value).flatMap(([key, nestedValue]) => [
            key,
            ...flattenPrimitiveValues(nestedValue, depth + 1),
        ]);
    }

    return [];
};

const getEventTextValues = (event) => [
    event.message,
    event.logentry?.message,
    event.culprit,
    event.transaction,
    event.request?.url,
    ...getExceptionValues(event).flatMap((exception) => [
        exception.type,
        exception.value,
    ]),
    ...getExceptionFrames(event).flatMap((frame) => [
        frame.filename,
        frame.abs_path,
        frame.module,
        frame.function,
    ]),
    ...flattenPrimitiveValues(event.breadcrumbs || []),
].filter(Boolean);

const shouldDropEvent = (event) => {
    const messages = [
        event.message,
        event.logentry?.message,
        ...getExceptionValues(event).flatMap((exception) => [
            exception.type,
            exception.value,
        ]),
    ];

    if (messages.some((message) => stringMatchesAny(message, IGNORED_MESSAGE_PATTERNS))) {
        return true;
    }

    if (getExceptionFrames(event).some((frame) =>
        [frame.filename, frame.abs_path, frame.module].some((source) =>
            stringMatchesAny(source, IGNORED_SOURCE_PATTERNS)
        )
    )) {
        return true;
    }

    const eventTextValues = getEventTextValues(event);
    const isGenericNetworkEvent = messages.some((message) =>
        stringMatchesAny(message, GENERIC_NETWORK_MESSAGE_PATTERNS)
    );
    const hasIgnoredThirdPartySource = eventTextValues.some((value) =>
        stringMatchesAny(value, IGNORED_SOURCE_PATTERNS)
    );
    const hasProtectedNetworkSource = eventTextValues.some((value) =>
        stringMatchesAny(value, PROTECTED_NETWORK_SOURCE_PATTERNS)
    );

    return isGenericNetworkEvent &&
        hasIgnoredThirdPartySource &&
        !hasProtectedNetworkSource;
};

export const scrubEvent = (event) => {
    if (shouldDropEvent(event)) {
        return null;
    }

    if (event.request) {
        delete event.request.cookies;
        delete event.request.data;

        if (event.request.headers) {
            delete event.request.headers.Authorization;
            delete event.request.headers.authorization;
            delete event.request.headers.Cookie;
            delete event.request.headers.cookie;
        }
    }

    event.extra = redactSensitiveData(event.extra || {});
    event.contexts = redactSensitiveData(event.contexts || {});

    if (Array.isArray(event.breadcrumbs)) {
        event.breadcrumbs = event.breadcrumbs
            .filter((breadcrumb) => breadcrumb.category !== "console")
            .map((breadcrumb) => redactSensitiveData(breadcrumb));
    }

    return event;
};

export function initSentry(app, router) {
    const dsn = process.env.VUE_APP_SENTRY_DSN || DEFAULT_SENTRY_DSN;
    const isDevMode = process.env.VUE_APP_DEV_MODE === "true";
    const enableInDev = process.env.VUE_APP_SENTRY_ENABLE_DEV === "true";

    if (!dsn || (isDevMode && !enableInDev)) {
        return;
    }

    Sentry.init({
        app,
        dsn,
        environment:
            process.env.VUE_APP_SENTRY_ENVIRONMENT ||
            (isDevMode ? "development" : "production"),
        release: process.env.VUE_APP_SENTRY_RELEASE || undefined,
        dataCollection: {
            userInfo: false,
            httpBodies: [],
        },
        enableLogs: true,
        maxBreadcrumbs: 20,
        integrations: [Sentry.browserTracingIntegration({ router })],
        tracesSampleRate: Number(process.env.VUE_APP_SENTRY_TRACES_SAMPLE_RATE || 0),
        beforeSend: scrubEvent,
    });
}

export function captureCustomizerMessage(message, options = {}) {
    const {
        level = "error",
        tags = {},
        context = {},
        extra = {},
        fingerprint = null,
    } = options;

    Sentry.withScope((scope) => {
        applyTags(scope, tags);
        // Groups one message into separate issues, e.g. one per failing backend feature.
        if (fingerprint) scope.setFingerprint(fingerprint);

        scope.setContext("customizer", getCustomizerContext(context));
        scope.setExtra("customizer", redactSensitiveData(extra));
        Sentry.captureMessage(message, { level });
    });
}

export function incrementCustomizerMetric(name, attributes = {}) {
    const safeAttributes = Object.entries(attributes).reduce(
        (result, [key, value]) => {
            result[key] = safeMetricAttributeValue(value);
            return result;
        },
        {}
    );

    Sentry.metrics.count(name, 1, {
        attributes: safeAttributes,
    });
}

export function captureCustomizerException(error, options = {}) {
    const { tags = {}, context = {}, extra = {} } = options;

    Sentry.withScope((scope) => {
        applyTags(scope, tags);

        scope.setContext("customizer", getCustomizerContext(context));
        scope.setExtra("customizer", redactSensitiveData(extra));
        Sentry.captureException(error);
    });
}
